import rawApiClient from './client';
import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import {
  getAISessionOwnershipHeaders,
  getScreenplayCoordinationIdentity,
  rememberScreenplaySession,
  rememberScreenplayCoordination,
  withAISessionCoordination,
} from './screenplay-coordination';
import { API_CONFIG } from '@/constants/config';
import { parseAiSessionSseEvents } from '@/lib/ai-session-sse';
import {
  createDurableSseState,
  decideDurableSseEvent,
  hasUnsupportedDurableSseSchema,
} from '@/lib/ai-session/protocol/durable-sse-reducer';
import { adaptCurrentWireSession } from '@/lib/ai-session/contracts/current-wire';
import type { CanonicalAISession } from '@/lib/ai-session/contracts/types';
import {
  applyCreateMessageContractSideEffect,
  buildCreateMessageRequestBody,
  parseCreateMessageResponse,
} from '@/lib/ai-session/protocol/create-message-stream';
import {
  createUnfinishedAiSessionStreamError,
  shouldContinueActiveAiSessionReconnect,
} from '@/lib/ai-session/protocol/stream-unfinished-recovery';
import { AISessionStreamError } from '@/lib/ai-session/protocol/stream-events/run-lifecycle';
import { sendWithUncertainWriteReplay } from '@/lib/ai-session/protocol/uncertain-write-replay';
import { generateUUID } from '@/lib/utils';
import { getBusinessErrorCode, getErrorCode } from '@/lib/error-utils';
import { errorTracker } from '@/lib/error-tracking';
import {
  AI_SESSION_MACHINE_CODE_USER_MESSAGES,
  isReactUpdateDepthError,
} from '@/lib/ai-session/contracts/utils';
import { isScreenplayVersionNotReadyError } from '@/lib/ai-session/workflow/screenplay-version-ready';
import { resolveRequestOrgId } from '@/lib/route-context-scope';
import type {
  AISessionActiveRunsResponse,
  AISessionAnswerBatchRequest,
  AISessionAnswerRequest,
  AISessionBillingAvailability,
  AISessionContextSuggestionsResponse,
  AISessionCreateMessageRequest,
  AISessionCreateMessageResponse,
  AISessionCreateRunRequest,
  AISessionCreateRunResponse,
  AISessionHistoryResponse,
  AISessionListMessagesResponse,
  AISessionModelOptionsResponse,
  AISessionRun,
  AISessionRunActionRequest,
  AISessionRunActionResponse,
  AISessionStageType,
  AISessionSkillOptionsResponse,
  AISessionSseEvent,
  AISessionAssetConfirmations,
  ScreenplayWorkflowArtifact,
  ScreenplayWorkflowSnapshot,
} from '@/types/ai-session';

const SOURCE_TYPE_SCREENPLAY = 1;
// 服务端每 15 秒发送心跳；连续三个周期无字节时重建连接，避免半开连接永久占用生成态。
const SSE_READ_SILENCE_TIMEOUT_MS = 45_000;

function ownershipConfig(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig
): AxiosRequestConfig | undefined {
  const headers = getAISessionOwnershipHeaders(url, data);
  if (Object.keys(headers).length === 0) return config;
  return { ...config, headers: { ...config?.headers, ...headers } };
}

const apiClient: Pick<AxiosInstance, 'get' | 'post' | 'put' | 'patch' | 'delete'> = {
  get: rawApiClient.get,
  post: (url, data, config) => {
    const ownedConfig = ownershipConfig(url, data, config);
    return withAISessionCoordination(url, data, () =>
      ownedConfig ? rawApiClient.post(url, data, ownedConfig) : rawApiClient.post(url, data)
    );
  },
  put: (url, data, config) =>
    withAISessionCoordination(url, data, () =>
      rawApiClient.put(url, data, ownershipConfig(url, data, config))
    ),
  patch: (url, data, config) =>
    withAISessionCoordination(url, data, () =>
      rawApiClient.patch(url, data, ownershipConfig(url, data, config))
    ),
  delete: (url, config) =>
    withAISessionCoordination(url, config?.data, () =>
      rawApiClient.delete(url, ownershipConfig(url, config?.data, config))
    ),
};

async function readSessionResponse(request: Promise<unknown>): Promise<CanonicalAISession> {
  const identity = getScreenplayCoordinationIdentity();
  const session = await request;
  const canonical = adaptCurrentWireSession(session);
  rememberScreenplaySession(canonical.sessionId, canonical.sourceId, identity);
  return canonical;
}

export type DraftReviewTarget =
  | { stage: 'storyboard'; shotNumber: number; draftItemId: string }
  | {
      stage: 'asset';
      group: 'characters' | 'locations' | 'props';
      draftItemId: string;
      variantDraftId?: string;
    };

async function reviewDraftItem(
  sessionId: string,
  target: DraftReviewTarget,
  reviewed: boolean,
  expectedVersion: number
): Promise<CanonicalAISession> {
  const body = { reviewed, draftItemId: target.draftItemId };
  return target.stage === 'storyboard'
    ? readSessionResponse(
        apiClient.patch(
          `/ai-sessions/${sessionId}/storyboard-breakdown/items/${target.shotNumber}/review`,
          { ...body, expectedStoryboardVersion: expectedVersion }
        )
      )
    : readSessionResponse(
        apiClient.patch(`/ai-sessions/${sessionId}/asset-breakdown/items/review`, {
          ...body,
          group: target.group,
          variantDraftId: target.variantDraftId,
          expectedAssetVersion: expectedVersion,
        })
      );
}

function getApiBaseUrl(): string {
  return String(import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
}

function buildApiUrl(path: string): string {
  const baseUrl = getApiBaseUrl();
  if (!baseUrl) return path;
  return `${baseUrl}${path}`;
}

function buildAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  const orgId = resolveRequestOrgId(
    window.location.pathname,
    sessionStorage.getItem('active_org_id')
  );
  if (orgId) headers['X-Active-Org-Id'] = orgId;
  return headers;
}

function isRetryableStreamHttpStatus(status: number): boolean {
  return status === 408 || status === 429 || status >= 500;
}

async function readSseResponse(
  response: Response,
  onEvent: (event: AISessionSseEvent) => boolean | void
): Promise<void> {
  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    const message =
      body && typeof body === 'object' && 'message' in body && typeof body.message === 'string'
        ? body.message
        : `AI 助手连接失败：HTTP ${response.status}`;
    throw new AISessionStreamError(message, {
      errorCode: getBusinessErrorCode(body) ?? 'AI_SESSION_STREAM_HTTP_ERROR',
      status: response.status,
      recoverable: isRetryableStreamHttpStatus(response.status),
    });
  }
  if (!response.body) {
    throw new Error('AI 助手连接失败：响应为空');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { value, done } = await new Promise<ReadableStreamReadResult<Uint8Array>>(
        (resolve, reject) => {
          const timeoutId = window.setTimeout(
            () => reject(new Error('AI 助手连接长时间无响应')),
            SSE_READ_SILENCE_TIMEOUT_MS
          );
          reader.read().then(
            (chunk) => {
              window.clearTimeout(timeoutId);
              resolve(chunk);
            },
            (error: unknown) => {
              window.clearTimeout(timeoutId);
              reject(error);
            }
          );
        }
      );
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const parts = buffer.split(/\n\n+/);
      buffer = parts.pop() ?? '';
      for (const event of parseAiSessionSseEvents(parts.join('\n\n'))) {
        if (onEvent(event) === false) {
          await reader.cancel().catch(() => undefined);
          return;
        }
      }
    }

    buffer += decoder.decode();
    for (const event of parseAiSessionSseEvents(buffer)) {
      if (onEvent(event) === false) {
        await reader.cancel().catch(() => undefined);
        return;
      }
    }
  } finally {
    await reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }
}

export function isTerminalAiSessionSseEvent(event: AISessionSseEvent): boolean {
  if (event.event === 'done' || event.event === 'error' || event.event === 'interaction_required') {
    return true;
  }
  // 直连 SSE 没有 relay id，以 completed/failed 作为终态；
  // relay SSE 需要继续等 worker commit 后发布的 done/error，避免 completed 后提交失败被吞掉。
  return !event.id && (event.event === 'completed' || event.event === 'failed');
}

function sleepWithAbort(ms: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) return Promise.resolve();
  return new Promise((resolve) => {
    const timeoutId = window.setTimeout(() => {
      signal?.removeEventListener('abort', handleAbort);
      resolve();
    }, ms);
    const handleAbort = () => {
      window.clearTimeout(timeoutId);
      resolve();
    };
    signal?.addEventListener('abort', handleAbort, { once: true });
  });
}

async function readSseWithReconnect(params: {
  url: URL;
  method: 'GET' | 'POST';
  headers?: HeadersInit;
  body?: BodyInit | null;
  signal?: AbortSignal;
  onEvent: (event: AISessionSseEvent) => void;
  onDurableSnapshot?: () => void | Promise<void>;
  shouldContinueAfterCleanClose?: () => boolean | Promise<boolean>;
  onConnectionState?: (state: 'connected' | 'reconnecting') => void;
}): Promise<void> {
  const MAX_RECONNECT = 5;
  const MAX_SNAPSHOT_RECONNECT = 3;
  let lastEventId: string | null = null;
  let finished = false;
  let snapshotRequested = false;
  let snapshotTask: Promise<void> | null = null;
  let snapshotEventId: string | null = null;
  let snapshotCanReconnect = false;
  let snapshotTerminal = false;
  let snapshotFailed = false;
  let snapshotUnsupported = false;
  let snapshotReconnects = 0;
  let attempts = 0;
  let activeContinues = 0;
  let durableState = createDurableSseState();

  while (!finished) {
    if (params.signal?.aborted) return;
    const headers = new Headers(params.headers);
    if (lastEventId) {
      headers.set('Last-Event-ID', lastEventId);
    }

    let streamError: unknown = null;
    let handlerError: unknown = null;
    try {
      const response = await fetch(params.url.toString(), {
        method: params.method,
        headers,
        body: params.body,
        signal: params.signal,
      });
      if (response.ok) params.onConnectionState?.('connected');

      await readSseResponse(response, (event) => {
        if (params.signal?.aborted) {
          finished = true;
          return false;
        }
        if (snapshotRequested) {
          return false;
        }
        const decision = decideDurableSseEvent(durableState, event);
        if (decision === 'drop') {
          return;
        }
        if (decision === 'snapshot') {
          snapshotRequested = true;
          finished = true;
          snapshotEventId = event.id ?? null;
          snapshotTerminal = isTerminalAiSessionSseEvent(event);
          snapshotFailed = event.event === 'error' || event.event === 'failed';
          snapshotUnsupported = hasUnsupportedDurableSseSchema(event);
          snapshotCanReconnect =
            Boolean(params.onDurableSnapshot && snapshotEventId) &&
            !snapshotUnsupported &&
            !snapshotTerminal;
          snapshotTask = Promise.resolve(params.onDurableSnapshot?.());
          return false;
        }
        if (event.id) {
          lastEventId = event.id;
          attempts = 0;
          snapshotReconnects = 0;
        }
        try {
          params.onEvent(event);
        } catch (error) {
          // 渲染死循环是前端缺陷，不是本轮 Run 失败：上报后继续消费，不进失败卡。
          // 其余异常（如 error 事件转成的 AISessionStreamError）仍按流失败抛出。
          if (isReactUpdateDepthError(error)) {
            errorTracker.captureException(error, { tags: { area: 'ai-session-stream' } });
          } else {
            handlerError = error;
            throw error;
          }
        }
        if (isTerminalAiSessionSseEvent(event)) {
          finished = true;
          return false;
        }
      });
    } catch (error) {
      if (params.signal?.aborted) return;
      if (handlerError) {
        throw handlerError;
      }
      // 明确的权限/业务拒绝不是连接中断，不能通过反复 GET 或 Run 探针绕过。
      if (error instanceof AISessionStreamError && !error.recoverable) throw error;
      streamError = error;
    }

    if (snapshotTask) {
      const pendingSnapshot = snapshotTask;
      snapshotTask = null;
      await pendingSnapshot;
      if (params.signal?.aborted) return;
      if (snapshotUnsupported && !snapshotTerminal) {
        throw new AISessionStreamError(
          AI_SESSION_MACHINE_CODE_USER_MESSAGES.CLIENT_VERSION_UNSUPPORTED,
          {
            errorCode: 'CLIENT_VERSION_UNSUPPORTED',
            recoverable: false,
          }
        );
      }

      snapshotReconnects += 1;
      if (
        !snapshotCanReconnect ||
        !snapshotEventId ||
        snapshotReconnects > MAX_SNAPSHOT_RECONNECT
      ) {
        // 重建快照不等于执行成功；失败终态仍须让调用方进入失败分支。
        if (snapshotTerminal) {
          if (snapshotFailed) throw new Error('AI 助手执行失败，请稍后重试。');
          return;
        }
        // 其余情况（重连预算耗尽、客户端 schema 过旧）后续事件不会再被消费，
        // 必须显式失败交由上层降级，不能静默假装流已正常结束。
        throw createUnfinishedAiSessionStreamError();
      }

      // 快照读取发生在 durable event 到达之后；用该事件作为已重建边界，
      // 再通过 Last-Event-ID 只订阅边界之后的新事件。
      lastEventId = snapshotEventId;
      durableState = createDurableSseState(snapshotEventId);
      snapshotRequested = false;
      snapshotEventId = null;
      snapshotCanReconnect = false;
      snapshotFailed = false;
      finished = false;
      attempts = 0;
      continue;
    }

    if (finished) break;
    if (params.signal?.aborted) return;
    params.onConnectionState?.('reconnecting');

    let continueWhileActive = false;
    if (params.shouldContinueAfterCleanClose) {
      try {
        continueWhileActive = Boolean(await params.shouldContinueAfterCleanClose());
      } catch (error) {
        const status = getErrorCode(error);
        if (status !== undefined && status >= 400 && !isRetryableStreamHttpStatus(status))
          throw error;
        continueWhileActive = false;
      }
    }
    if (continueWhileActive && shouldContinueActiveAiSessionReconnect(activeContinues)) {
      activeContinues += 1;
      attempts = 0;
      await sleepWithAbort(Math.min(1000 * activeContinues, 5000), params.signal);
      continue;
    }

    if (streamError) {
      if (attempts >= MAX_RECONNECT) {
        throw createUnfinishedAiSessionStreamError(streamError);
      }
      attempts += 1;
      await sleepWithAbort(Math.min(1000 * attempts, 5000), params.signal);
      continue;
    }

    if (attempts >= MAX_RECONNECT) break;
    attempts += 1;
    await sleepWithAbort(Math.min(1000 * attempts, 5000), params.signal);
  }

  if (!finished && !params.signal?.aborted) {
    throw createUnfinishedAiSessionStreamError();
  }
}

export const aiSessionsApi = {
  reviewDraftItem,
  async getModelOptions(): Promise<AISessionModelOptionsResponse> {
    return apiClient.get('/ai-sessions/model-options') as Promise<AISessionModelOptionsResponse>;
  },

  async getSkillOptionsForScreenplay(screenplayId: string): Promise<AISessionSkillOptionsResponse> {
    const params = new URLSearchParams({
      sourceType: String(SOURCE_TYPE_SCREENPLAY),
      sourceId: screenplayId,
    });
    return apiClient.get(
      `/ai-sessions/skill-options?${params.toString()}`
    ) as Promise<AISessionSkillOptionsResponse>;
  },

  async createOrResumeForScreenplay(
    screenplayId: string,
    options?: {
      forceNew?: boolean;
      selectedSkillId?: string | null;
      selectedSkillVersionId?: string | null;
      signal?: AbortSignal;
    }
  ): Promise<CanonicalAISession> {
    const maxAttempts = 4;
    let lastError: unknown;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      options?.signal?.throwIfAborted();
      try {
        return await readSessionResponse(
          apiClient.post(
            '/ai-sessions',
            {
              sourceType: SOURCE_TYPE_SCREENPLAY,
              sourceId: screenplayId,
              forceNew: Boolean(options?.forceNew),
              selectedSkillId: options?.selectedSkillId ?? null,
              selectedSkillVersionId: options?.selectedSkillVersionId ?? null,
            },
            options?.signal ? { signal: options.signal } : undefined
          )
        );
      } catch (error) {
        lastError = error;
        if (!isScreenplayVersionNotReadyError(error) || attempt === maxAttempts - 1) {
          throw error;
        }
        await sleepWithAbort(1500, options?.signal);
      }
    }
    throw lastError;
  },

  async authorizeSession(
    sessionId: string,
    options?: { signal?: AbortSignal }
  ): Promise<CanonicalAISession> {
    return readSessionResponse(
      apiClient.post(`/ai-sessions/${sessionId}/authorize`, undefined, options)
    );
  },

  async getSession(
    sessionId: string,
    options?: { signal?: AbortSignal }
  ): Promise<CanonicalAISession> {
    return readSessionResponse(apiClient.get(`/ai-sessions/${sessionId}`, options));
  },

  async getBillingAvailability(sessionId: string): Promise<AISessionBillingAvailability> {
    return apiClient.get(
      `/ai-sessions/${sessionId}/billing-availability`
    ) as Promise<AISessionBillingAvailability>;
  },

  async getWorkflowSnapshot(screenplayId: string): Promise<ScreenplayWorkflowSnapshot> {
    const identity = getScreenplayCoordinationIdentity();
    const snapshot = (await apiClient.get(`/screenplay-workflows/${screenplayId}/snapshot`, {
      params: { includeArtifacts: false },
    })) as unknown as ScreenplayWorkflowSnapshot;
    return snapshot.coordination
      ? {
          ...snapshot,
          coordination: rememberScreenplayCoordination(snapshot.coordination, identity),
        }
      : snapshot;
  },

  async saveInitialBreakdownSuggestion(
    screenplayId: string,
    data: { sessionId: string; content?: string | null; confirm?: boolean }
  ): Promise<ScreenplayWorkflowArtifact> {
    return apiClient.post(`/screenplay-workflows/${screenplayId}/initial-breakdown-suggestion`, {
      sessionId: data.sessionId,
      content: data.content ?? null,
      confirm: Boolean(data.confirm),
    }) as Promise<ScreenplayWorkflowArtifact>;
  },

  async listMessages(
    sessionId: string,
    options?: { signal?: AbortSignal }
  ): Promise<AISessionListMessagesResponse> {
    return apiClient.get(
      `/ai-sessions/${sessionId}/messages`,
      options
    ) as Promise<AISessionListMessagesResponse>;
  },

  async createMessage(
    sessionId: string,
    request: AISessionCreateMessageRequest,
    options?: { signal?: AbortSignal }
  ): Promise<AISessionCreateMessageResponse> {
    try {
      const response = await apiClient.post(
        `/ai-sessions/${sessionId}/messages`,
        buildCreateMessageRequestBody(request, generateUUID),
        {
          timeout: API_CONFIG.AI_SESSION_MUTATION_TIMEOUT,
          ...(options?.signal ? { signal: options.signal } : {}),
        }
      );
      return parseCreateMessageResponse(response);
    } catch (error) {
      applyCreateMessageContractSideEffect(getBusinessErrorCode(error));
      throw error;
    }
  },

  async createRun(
    sessionId: string,
    request: AISessionCreateRunRequest
  ): Promise<AISessionCreateRunResponse> {
    return apiClient.post(
      `/ai-sessions/${sessionId}/runs`,
      {
        runType: request.runType,
        modelName: request.modelName ?? null,
        responseMode: request.responseMode ?? null,
        selectedSkillId: request.selectedSkillId ?? null,
        selectedSkillVersionId: request.selectedSkillVersionId ?? null,
        rerunInstruction: request.rerunInstruction ?? null,
        ...(request.forceNew !== undefined ? { forceNew: request.forceNew } : {}),
        ...(request.expectedBaseVersion !== undefined
          ? { expectedBaseVersion: request.expectedBaseVersion }
          : {}),
        sceneOrder: request.sceneOrder ?? null,
        opinion: request.opinion ?? null,
        requestIdempotencyKey: request.requestIdempotencyKey ?? generateUUID(),
      },
      { timeout: API_CONFIG.AI_SESSION_MUTATION_TIMEOUT }
    ) as Promise<AISessionCreateRunResponse>;
  },

  async updateEditSelection(
    sessionId: string,
    runId: string,
    request: {
      expectedRunVersion: number;
      choices: Record<
        string,
        import('@/lib/ai-session/presentation/run-projection').EditSelectionChoice
      >;
    }
  ): Promise<AISessionRun> {
    return apiClient.put(
      `/ai-sessions/${sessionId}/runs/${runId}/edit-selection`,
      request
    ) as Promise<AISessionRun>;
  },

  async getRun(sessionId: string, runId: string): Promise<AISessionRun> {
    return apiClient.get(`/ai-sessions/${sessionId}/runs/${runId}`) as Promise<AISessionRun>;
  },

  async listActiveRuns(
    sessionId: string,
    options?: { signal?: AbortSignal }
  ): Promise<AISessionActiveRunsResponse> {
    return apiClient.get(
      `/ai-sessions/${sessionId}/runs`,
      options
    ) as Promise<AISessionActiveRunsResponse>;
  },

  async createRunAction(
    sessionId: string,
    runId: string,
    request: AISessionRunActionRequest
  ): Promise<AISessionRunActionResponse> {
    return apiClient.post(`/ai-sessions/${sessionId}/runs/${runId}/actions`, {
      actionType: request.actionType,
      idempotencyKey: request.idempotencyKey,
      expectedRunStatus: request.expectedRunStatus,
      expectedRunVersion: request.expectedRunVersion,
      ...(request.modelName ? { modelName: request.modelName } : {}),
      ...(request.expectedPlanHash ? { expectedPlanHash: request.expectedPlanHash } : {}),
    }) as Promise<AISessionRunActionResponse>;
  },

  async listHistoryForScreenplay(screenplayId: string): Promise<AISessionHistoryResponse> {
    const params = new URLSearchParams({
      sourceType: String(SOURCE_TYPE_SCREENPLAY),
      sourceId: screenplayId,
    });
    return apiClient.get(
      `/ai-sessions/history?${params.toString()}`
    ) as Promise<AISessionHistoryResponse>;
  },

  async getContextSuggestions(
    sessionId: string,
    options?: { signal?: AbortSignal }
  ): Promise<AISessionContextSuggestionsResponse> {
    return apiClient.get(
      `/ai-sessions/${sessionId}/context-suggestions`,
      options
    ) as Promise<AISessionContextSuggestionsResponse>;
  },

  async referenceContextSessions(
    sessionId: string,
    referencedSessionIds: string[]
  ): Promise<CanonicalAISession> {
    return readSessionResponse(
      apiClient.post(`/ai-sessions/${sessionId}/context-references`, {
        referencedSessionIds,
      })
    );
  },

  async submitAnswer(
    sessionId: string,
    data: AISessionAnswerRequest,
    options?: { signal?: AbortSignal }
  ): Promise<CanonicalAISession> {
    return readSessionResponse(
      apiClient.post(
        `/ai-sessions/${sessionId}/answers`,
        {
          ...data,
          requestIdempotencyKey: data.requestIdempotencyKey ?? generateUUID(),
        },
        options
      )
    );
  },

  async submitAnswers(
    sessionId: string,
    data: AISessionAnswerBatchRequest,
    options?: { signal?: AbortSignal }
  ): Promise<CanonicalAISession> {
    return readSessionResponse(
      apiClient.post(
        `/ai-sessions/${sessionId}/answers/batch`,
        {
          ...data,
          requestIdempotencyKey: data.requestIdempotencyKey ?? generateUUID(),
        },
        options
      )
    );
  },

  async selectSkill(
    sessionId: string,
    skillId: string,
    selectedSkillVersionId?: string | null
  ): Promise<CanonicalAISession> {
    return readSessionResponse(
      apiClient.post(`/ai-sessions/${sessionId}/skill`, {
        skillId,
        selectedSkillVersionId: selectedSkillVersionId ?? null,
      })
    );
  },

  async prepareStageSession(request: {
    sourceSessionId: string;
    stageType: AISessionStageType;
  }): Promise<CanonicalAISession> {
    return readSessionResponse(apiClient.post('/ai-sessions/stage-sessions', request));
  },

  async freezeAssetChecklist(
    sessionId: string,
    confirmations: AISessionAssetConfirmations
  ): Promise<CanonicalAISession> {
    return sendWithUncertainWriteReplay(() =>
      readSessionResponse(
        apiClient.post(`/ai-sessions/${sessionId}/asset-breakdown/freeze-checklist`, {
          confirmations,
        })
      )
    );
  },

  async streamAssetDetailExtraction(
    sessionId: string,
    handlers: {
      modelName?: string | null;
      rerunInstruction?: string | null;
      signal?: AbortSignal;
      onEvent: (event: AISessionSseEvent) => void;
    }
  ): Promise<AISessionCreateRunResponse> {
    return aiSessionsApi.createRun(sessionId, {
      runType: 'asset_detail_extraction',
      modelName: handlers.modelName,
      rerunInstruction: handlers.rerunInstruction?.trim() || null,
    });
  },

  async persistAssetBreakdown(
    sessionId: string,
    confirmations: AISessionAssetConfirmations,
    expectedAssetVersion: number
  ): Promise<CanonicalAISession> {
    return sendWithUncertainWriteReplay(() =>
      readSessionResponse(
        apiClient.post(`/ai-sessions/${sessionId}/asset-breakdown/persist`, {
          confirmations,
          expectedAssetVersion,
        })
      )
    );
  },

  async suspendAsset(screenplayId: string): Promise<{ status: string; message: string }> {
    return apiClient.post(`/screenplay-workflows/${screenplayId}/asset/suspend`) as Promise<{
      status: string;
      message: string;
    }>;
  },

  async resumeAsset(screenplayId: string): Promise<{ status: string; message: string }> {
    return apiClient.post(`/screenplay-workflows/${screenplayId}/asset/resume`) as Promise<{
      status: string;
      message: string;
    }>;
  },

  async deleteAssetItem(
    sessionId: string,
    group: 'characters' | 'locations' | 'props',
    name: string,
    options?: { variantDraftId?: string; expectedAssetVersion?: number; draftItemId?: string }
  ): Promise<CanonicalAISession> {
    return readSessionResponse(
      apiClient.delete(`/ai-sessions/${sessionId}/asset-breakdown/items`, {
        data: {
          group,
          name,
          ...(options?.variantDraftId ? { variantDraftId: options.variantDraftId } : {}),
          ...(options?.expectedAssetVersion !== undefined
            ? { expectedAssetVersion: options.expectedAssetVersion }
            : {}),
          ...(options?.draftItemId ? { draftItemId: options.draftItemId } : {}),
        },
      })
    );
  },

  async addAssetItem(
    sessionId: string,
    group: 'characters' | 'locations' | 'props',
    name: string,
    description?: string
  ): Promise<CanonicalAISession> {
    return readSessionResponse(
      apiClient.post(`/ai-sessions/${sessionId}/asset-breakdown/items`, {
        group,
        name,
        description,
      })
    );
  },

  async updateAssetItem(
    sessionId: string,
    group: 'characters' | 'locations' | 'props',
    name: string,
    patch: Record<string, unknown>,
    expectedAssetVersion?: number,
    draftItemId?: string
  ): Promise<CanonicalAISession> {
    return readSessionResponse(
      apiClient.patch(`/ai-sessions/${sessionId}/asset-breakdown/items`, {
        group,
        name,
        patch,
        ...(expectedAssetVersion !== undefined ? { expectedAssetVersion } : {}),
        ...(draftItemId ? { draftItemId } : {}),
      })
    );
  },

  async updateStoryboardItem(
    sessionId: string,
    shotNumber: number,
    patch: Record<string, string>,
    expectedStoryboardVersion: number,
    draftItemId?: string
  ): Promise<CanonicalAISession> {
    return readSessionResponse(
      apiClient.patch(`/ai-sessions/${sessionId}/storyboard-breakdown/items/${shotNumber}`, {
        patch,
        expectedStoryboardVersion,
        ...(draftItemId ? { draftItemId } : {}),
      })
    );
  },

  async reviewStoryboardItem(
    sessionId: string,
    shotNumber: number,
    reviewed: boolean,
    expectedStoryboardVersion: number,
    draftItemId: string
  ): Promise<CanonicalAISession> {
    return reviewDraftItem(
      sessionId,
      { stage: 'storyboard', shotNumber, draftItemId },
      reviewed,
      expectedStoryboardVersion
    );
  },

  async deleteStoryboardItem(
    sessionId: string,
    shotNumber: number,
    expectedStoryboardVersion: number
  ): Promise<CanonicalAISession> {
    return readSessionResponse(
      apiClient.delete(`/ai-sessions/${sessionId}/storyboard-breakdown/items/${shotNumber}`, {
        params: { expectedStoryboardVersion },
      })
    );
  },

  async streamStoryboardMissingSceneRetry(
    sessionId: string,
    handlers: {
      modelName?: string | null;
      signal?: AbortSignal;
      onEvent: (event: AISessionSseEvent) => void;
    }
  ): Promise<AISessionCreateRunResponse> {
    return aiSessionsApi.createRun(sessionId, {
      runType: 'storyboard_missing_scene_retry',
      modelName: handlers.modelName,
    });
  },

  async streamStoryboardSceneRegenerate(
    sessionId: string,
    handlers: {
      sceneOrder: number;
      opinion?: string;
      modelName?: string | null;
      signal?: AbortSignal;
      onEvent: (event: AISessionSseEvent) => void;
    }
  ): Promise<AISessionCreateRunResponse> {
    return aiSessionsApi.createRun(sessionId, {
      runType: 'storyboard_scene_regenerate',
      modelName: handlers.modelName,
      sceneOrder: handlers.sceneOrder,
      opinion: handlers.opinion ?? '',
    });
  },

  async restorePreviousStoryboard(
    sessionId: string,
    expectedBaseVersion: number
  ): Promise<CanonicalAISession> {
    return readSessionResponse(
      apiClient.post(`/ai-sessions/${sessionId}/storyboard-breakdown/restore-previous`, {
        expectedBaseVersion,
      })
    );
  },

  async confirmStoryboardBreakdown(
    sessionId: string,
    expectedStoryboardVersion: number
  ): Promise<CanonicalAISession> {
    return sendWithUncertainWriteReplay(() =>
      readSessionResponse(
        apiClient.post(
          `/ai-sessions/${sessionId}/storyboard-breakdown/confirm`,
          { expectedStoryboardVersion },
          { timeout: API_CONFIG.AI_SESSION_MUTATION_TIMEOUT }
        )
      )
    );
  },

  async confirmStoryboardStructure(
    sessionId: string,
    c1Version: number
  ): Promise<CanonicalAISession> {
    return sendWithUncertainWriteReplay(() =>
      readSessionResponse(
        apiClient.post(`/ai-sessions/${sessionId}/storyboard-breakdown/structure/confirm`, {
          c1Version,
        })
      )
    );
  },

  async skipStoryboardStructure(sessionId: string, c1Version: number): Promise<CanonicalAISession> {
    return sendWithUncertainWriteReplay(() =>
      readSessionResponse(
        apiClient.post(`/ai-sessions/${sessionId}/storyboard-breakdown/structure/skip`, {
          c1Version,
          riskAcknowledged: true,
        })
      )
    );
  },

  async regenerateStoryboardStructure(
    sessionId: string,
    sceneId: string,
    opinion: string
  ): Promise<CanonicalAISession> {
    return readSessionResponse(
      apiClient.post(`/ai-sessions/${sessionId}/storyboard-breakdown/structure/regenerate`, {
        sceneId,
        opinion,
      })
    );
  },

  async streamRun(
    sessionId: string,
    runId: string,
    handlers: {
      signal?: AbortSignal;
      onEvent: (event: AISessionSseEvent) => void;
      onDurableSnapshot?: () => void | Promise<void>;
      shouldContinueAfterCleanClose?: () => boolean | Promise<boolean>;
      onConnectionState?: (state: 'connected' | 'reconnecting') => void;
    }
  ): Promise<void> {
    const url = new URL(
      buildApiUrl(`/ai-sessions/${sessionId}/runs/${runId}/stream`),
      window.location.origin
    );
    await readSseWithReconnect({
      url,
      method: 'GET',
      headers: buildAuthHeaders(),
      signal: handlers.signal,
      onEvent: handlers.onEvent,
      onConnectionState: handlers.onConnectionState,
      onDurableSnapshot: handlers.onDurableSnapshot,
      shouldContinueAfterCleanClose: handlers.shouldContinueAfterCleanClose,
    });
  },
};
